import { useReducer, useCallback, useEffect, useRef } from 'react';
import {
  generatePuzzle,
  validateMove,
  isSolved,
  type BoardState,
} from '@/utils/sudokuGenerator';
import { type Difficulty } from '@/game/configs';
export type GameStatus = 'idle' | 'playing' | 'paused' | 'won' | 'lost';
interface UndoState {
  board: BoardState;
  selectedCell: number | null;
}
interface GameState {
  board: BoardState;
  selectedCell: number | null;
  notesMode: boolean;
  mistakes: number;
  timerSeconds: number;
  gameStatus: GameStatus;
  difficulty: Difficulty;
  undoStack: UndoState[];
  startTime: number | null;
}
type GameAction =
  | { type: 'SELECT_CELL'; payload: number }
  | { type: 'INPUT_NUMBER'; payload: number }
  | { type: 'TOGGLE_NOTE' }
  | { type: 'ERASE' }
  | { type: 'UNDO' }
  | { type: 'TICK_TIMER' }
  | { type: 'SET_DIFFICULTY'; payload: Difficulty }
  | { type: 'START_GAME' }
  | { type: 'PAUSE_GAME' }
  | { type: 'RESUME_GAME' }
  | { type: 'WIN_GAME' }
  | { type: 'LOSE_GAME' }
  | { type: 'RESET_GAME' };
const initialState: GameState = {
  board: Array.from({ length: 81 }, () => ({
    value: null,
    isGiven: false,
    notes: [],
  })),
  selectedCell: null,
  notesMode: false,
  mistakes: 0,
  timerSeconds: 0,
  gameStatus: 'idle',
  difficulty: 'easy',
  undoStack: [],
  startTime: null,
};
const createInitialBoard = (difficulty: Difficulty): BoardState => {
  return generatePuzzle(difficulty);
};
const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'SELECT_CELL': {
      const cell = state.board[action.payload];
      // Prevent selecting given cells - they are read-only
      if (cell.isGiven) {
        return state;
      }
      return {
        ...state,
        selectedCell: action.payload,
      };
    }
    case 'INPUT_NUMBER': {
      if (state.selectedCell === null || state.gameStatus !== 'playing') {
        return state;
      }
      const cell = state.board[state.selectedCell];
      if (cell.isGiven) {
        return state;
      }
      const undoState: UndoState = {
        board: JSON.parse(JSON.stringify(state.board)),
        selectedCell: state.selectedCell,
      };
      const newBoard = JSON.parse(JSON.stringify(state.board)) as BoardState;
      const currentCell = newBoard[state.selectedCell];
      if (state.notesMode) {
        const noteIndex = currentCell.notes.indexOf(action.payload);
        if (noteIndex > -1) {
          currentCell.notes.splice(noteIndex, 1);
        } else {
          currentCell.notes.push(action.payload);
          currentCell.notes.sort((a, b) => a - b);
        }
        currentCell.value = null;
        return {
          ...state,
          board: newBoard,
          undoStack: [...state.undoStack, undoState],
        };
      } else {
        currentCell.value = action.payload;
        currentCell.notes = [];
        if (!validateMove(newBoard, state.selectedCell, action.payload)) {
          const newMistakes = state.mistakes + 1;
          return {
            ...state,
            board: newBoard,
            mistakes: newMistakes,
            undoStack: [...state.undoStack, undoState],
            gameStatus: newMistakes >= 3 ? 'lost' : state.gameStatus,
          };
        }
        if (isSolved(newBoard)) {
          return {
            ...state,
            board: newBoard,
            gameStatus: 'won',
            undoStack: [...state.undoStack, undoState],
          };
        }
        return {
          ...state,
          board: newBoard,
          undoStack: [...state.undoStack, undoState],
        };
      }
    }
    case 'TOGGLE_NOTE':
      return {
        ...state,
        notesMode: !state.notesMode,
      };
    case 'ERASE': {
      if (state.selectedCell === null || state.gameStatus !== 'playing') {
        return state;
      }
      const cell = state.board[state.selectedCell];
      if (cell.isGiven) {
        return state;
      }
      const undoState: UndoState = {
        board: JSON.parse(JSON.stringify(state.board)),
        selectedCell: state.selectedCell,
      };
      const newBoard = JSON.parse(JSON.stringify(state.board)) as BoardState;
      newBoard[state.selectedCell] = {
        ...newBoard[state.selectedCell],
        value: null,
        notes: [],
      };
      return {
        ...state,
        board: newBoard,
        undoStack: [...state.undoStack, undoState],
      };
    }
    case 'UNDO': {
      if (state.undoStack.length === 0 || state.gameStatus !== 'playing') {
        return state;
      }
      const previousState = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        board: previousState.board,
        selectedCell: previousState.selectedCell,
        undoStack: state.undoStack.slice(0, -1),
      };
    }
    case 'TICK_TIMER':
      if (state.gameStatus !== 'playing') return state;
      return {
        ...state,
        timerSeconds: state.timerSeconds + 1,
      };
    case 'SET_DIFFICULTY':
      return {
        ...initialState,
        difficulty: action.payload,
        gameStatus: 'idle',
      };
    case 'START_GAME':
      return {
        ...initialState,
        board: createInitialBoard(state.difficulty),
        difficulty: state.difficulty,
        gameStatus: 'playing',
        startTime: Date.now(),
      };
    case 'PAUSE_GAME':
      if (state.gameStatus !== 'playing') return state;
      return {
        ...state,
        gameStatus: 'paused',
      };
    case 'RESUME_GAME':
      if (state.gameStatus !== 'paused') return state;
      return {
        ...state,
        gameStatus: 'playing',
      };
    case 'WIN_GAME':
      return {
        ...state,
        gameStatus: 'won',
      };
    case 'LOSE_GAME':
      return {
        ...state,
        gameStatus: 'lost',
      };
    case 'RESET_GAME':
      return {
        ...initialState,
        difficulty: state.difficulty,
      };
    default:
      return state;
  }
};
export const useSudokuGame = () => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (state.gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        dispatch({ type: 'TICK_TIMER' });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [state.gameStatus]);
  const selectCell = useCallback((index: number) => {
    dispatch({ type: 'SELECT_CELL', payload: index });
  }, []);
  const inputNumber = useCallback((num: number) => {
    dispatch({ type: 'INPUT_NUMBER', payload: num });
  }, []);
  const toggleNote = useCallback(() => {
    dispatch({ type: 'TOGGLE_NOTE' });
  }, []);
  const erase = useCallback(() => {
    dispatch({ type: 'ERASE' });
  }, []);
  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);
  const setDifficulty = useCallback((difficulty: Difficulty) => {
    dispatch({ type: 'SET_DIFFICULTY', payload: difficulty });
  }, []);
  const startGame = useCallback(() => {
    dispatch({ type: 'START_GAME' });
  }, []);
  const pauseGame = useCallback(() => {
    dispatch({ type: 'PAUSE_GAME' });
  }, []);
  const resumeGame = useCallback(() => {
    dispatch({ type: 'RESUME_GAME' });
  }, []);
  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
  }, []);
  return {
    ...state,
    selectCell,
    inputNumber,
    toggleNote,
    erase,
    undo,
    setDifficulty,
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
  };
};